import { PermissionGate } from "@/components/common/PermissionGate"
import { PERMISSIONS } from "@/constants/permissions"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Link } from "react-router"
import {
  useDeclarationInquiryCoverage,
  type InquiryRegulationCoverage,
} from "../../../../../api/declarationInquiriesApi"
import {
  normalizeFrameworkCode,
  regulationLabel,
  regulationShortLabel,
} from "../../../../../utils/regulationLabel"
import { McPill, type McPillTone } from "../../../../shared/McPill"
import { buildRequestSearchParams } from "../footer/requestContext"

const STATE: Record<
  InquiryRegulationCoverage["state"],
  { tone: McPillTone; label: TranslationKeys }
> = {
  delivered: { tone: "success", label: "MC_DECL_V2_COVERAGE_DELIVERED" },
  missing: { tone: "attention", label: "MC_DECL_V2_COVERAGE_MISSING" },
  extra: { tone: "muted", label: "MC_DECL_V2_COVERAGE_EXTRA" },
}

/**
 * Asked against delivered for the supplier inquiry this declaration answers
 * (TUR-1732): one pill per regulation, delivered, missing or not asked, counted
 * over every reply to the inquiry, and a Rückfrage with the missing ones
 * preselected. A strip under the breadcrumbs, so it shows for a reply to one
 * regulation too, where the navigator is hidden. Renders nothing when there is
 * nothing to compare (no linked inquiry, or one without a stored scope).
 */
export function InquiryCoverageHint({
  declarationId,
  declarationNumber,
  supplierId,
}: {
  declarationId: string
  declarationNumber: string | null
  supplierId: string | null | undefined
}) {
  const { translate } = useTranslation()
  const { data: coverage } = useDeclarationInquiryCoverage(declarationId)
  if (!coverage?.regulations.length) return null

  const missing = coverage.regulations
    .filter((r) => r.state === "missing")
    .map((r) => r.key)
  const missingList = missing
    .map((key) => regulationLabel(normalizeFrameworkCode(key), key, translate))
    .join(", ")
  // A follow-up to the supplier for what the inquiry still lacks, not a question about
  // this declaration: no `declarationId`, so it is not saved as the declaration's own
  // Rückfrage, which would read as a question batch and hold back its approval for
  // every regulation. The Bezug line still names the declaration.
  const requestParams = buildRequestSearchParams({
    supplierId,
    regulations: missing,
  })
  requestParams.append(
    "context",
    translate("MC_DECL_V2_COVERAGE_REQUEST_LINE", {
      declaration: declarationNumber ?? "—",
      list: missingList,
    }),
  )
  const requestHref =
    missing.length && supplierId
      ? `/material-compliance/supplier-studio/inquiry/new?${requestParams.toString()}`
      : null

  return (
    <div
      className="sv2-siblinghint sv2-siblinghint--card sv2-siblinghint--inline flex flex-wrap items-center gap-1.5"
      data-testid="sv2-inquiry-coverage"
    >
      <span>
        {translate("MC_DECL_V2_COVERAGE_TITLE", {
          reference: coverage.reference ?? "—",
        })}
      </span>
      {coverage.regulations.map((r) => {
        const code = normalizeFrameworkCode(r.key)
        return (
          <McPill
            key={r.key}
            tone={STATE[r.state].tone}
            title={regulationLabel(code, r.key, translate)}
          >
            {`${regulationShortLabel(code, r.key, translate)} · ${translate(STATE[r.state].label)}`}
          </McPill>
        )
      })}
      {requestHref && (
        // A request creates an inquiry: only for whoever may send one.
        <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_SEND}>
          <Link to={requestHref} className="ml-auto underline">
            {translate("MC_DECL_V2_COVERAGE_REQUEST", { list: missingList })}
          </Link>
        </PermissionGate>
      )}
    </div>
  )
}
